<template>
  <main id="main-content">
    <div class="feature-landing-page">
      <div class="content-page-banner">
        <div class="banner-content">
          <h1>{{ $route.meta.title }}</h1>
          <div class="underline"></div>
          <div v-if="isPolicyHolder && !isCoverStartDateEqualToCurrentDate && content && content.topContent" v-html="content.topContent" />
        </div>
      </div>

      <section class="content fr-view">
        <ApiLoading v-if="apiStatus.refData.loading || apiStatus.refData.fail" :apiError="apiStatus.refData.fail"></ApiLoading>
        <template v-else-if="isPolicyHolderOrPartnerAContributor || isPolicyHolderOrPartnerAResponsibleAdult">
          <div>
            <p>If you would like to make changes to your product or add or remove a person on this policy, please contact Defence Health.</p>
          </div>
        </template>
        <template v-else-if="content !== null">
          <!-- Not policy holder access alternate content -->
          <div v-if="!isPolicyHolder" v-html="content.accessDeniedErrorText" />
          <!-- Can't make change since Cover start date is today alternate content -->
          <div v-else-if="isCoverStartDateEqualToCurrentDate" v-html="content.startDateEqualToCurrentDateErrorText" />
          <!-- Landing page content with change my product and add/remove person links -->
          <div id="page-content" v-else>
            <!-- Subheading -->
            <div id="page-text">
              <h2 v-if="content.subHeading" v-html="content.subHeading" />
              <div class="section-description" v-html="content.subHeadingContent" />
              <div class="section-description" v-html="content.changeProductContent" />
              <div class="section-description" v-html="content.addRemovePersonContent"></div>
              <div class="section" v-if="content.viewMemberDetailsContent">
                <div class="section-description" v-html="content.viewMemberDetailsContent"></div>
              </div>
            </div>

            <div id="page-buttons">
              <!-- Change your product -->
              <TaskButton
                v-if="content.changeProductButton"
                :imageSrc="getImageUrl('/assets/public/images/icon_changeproduct.svg')"
                imageAlt="change-product-icon"
                :hasArrow="true"
                :onClick="() => navigateToPage(CHANGE_MY_PRODUCT, { isFromLandingPage: true, coverDetails: membershipInfo })"
                :enterDown="() => navigateToPage(CHANGE_MY_PRODUCT, { isFromLandingPage: true, coverDetails: membershipInfo })"
                tabindex="0"
              >
                {{ content.changeProductButton }}
              </TaskButton>

              <!-- Add remove person -->
              <TaskButton
                v-if="content.changeProductButton"
                :imageSrc="getImageUrl('/assets/public/images/icon_addremoveperson.svg')"
                imageAlt="add-remove-person-icon"
                :hasArrow="true"
                :onClick="() => navigateToPage(ADD_REMOVE_PERSON, { isFromLandingPage: true, coverDetails: membershipInfo })"
                :enterDown="() => navigateToPage(ADD_REMOVE_PERSON, { isFromLandingPage: true, coverDetails: membershipInfo })"
                tabindex="0"
              >
                {{ content.addRemovePersonButton }}
              </TaskButton>
            </div>

            <!-- View member details -->
          </div>
        </template>
      </section>
    </div>
  </main>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex';
import { ChangeCover as Services } from '@/services/MyCover';
import { ApiLoading } from '@/components';
import { CHANGE_MY_PRODUCT, ADD_REMOVE_PERSON } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { getImageUrl } from '@/utils';
import { VUEX_GENERAL } from '@/constants/VuexConstants';
import { getAccessLevel, findPolicyHolder, getPartnerDetailsMP } from '@/utils/MemberUtils';
import { AccessLevel, InsurableTypeEnum } from '@/enums';
import { checkWithinXDays } from '@/utils/DateTimeUtils';
import TaskButton from '@general/js/components/TaskButton.vue';
import ChangeProductServices from '@/services/MyCover/ChangeCover/ChangeProduct';

export default {
  name: 'ChangeCoverLanding',

  components: {
    ApiLoading,
    TaskButton,
  },

  data() {
    return {
      CHANGE_MY_PRODUCT,
      ADD_REMOVE_PERSON,
      apiStatus: {
        refData: {
          fail: false,
          loading: false,
        },
      },
      content: null,
      pagination: {
        pageNum: 1,
        pageSize: 10,
        totalNum: 0,
        lastPageNum: 1,
      },
      membershipInfo: null,
      memberDetails: [],
    };
  },

  mounted() {
    this.getReferenceData();
  },

  computed: {
    ...mapGetters({
      memberNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      currentMemberDetail: VUEX_GENERAL.GETTERS.getMembershipInfo,
      memberships: VUEX_GENERAL.GETTERS.memberships,
    }),

    accessLevel() {
      return getAccessLevel(this.memberNumber, this.memberships)?.toLowerCase();
    },

    isPolicyHolder() {
      return this.accessLevel === AccessLevel.POLICY_HOLDER_ACCESS;
    },

    // checks if cover start date is equal to the current date
    isCoverStartDateEqualToCurrentDate() {
      return checkWithinXDays(this.currentMemberDetail?.coverStartDate, 0);
    },

    isPolicyHolderOrPartnerAContributor() {
      return this.isPolicyHolderAContributor || this.isPartnerAContributor;
    },

    isPolicyHolderAContributor() {
      const policyHolder = findPolicyHolder(this.memberDetails, false, true);
      return policyHolder?.insurableType === InsurableTypeEnum.CONTRIBUTOR;
    },

    isPartnerAContributor() {
      const partner = getPartnerDetailsMP(this.memberDetails);
      return partner?.insurableType === InsurableTypeEnum.CONTRIBUTOR;
    },

    isPolicyHolderOrPartnerAResponsibleAdult() {
      return this.isPolicyHolderAResponsibleAdult || this.isPartnerAResponsibleAdult;
    },

    isPolicyHolderAResponsibleAdult() {
      const policyHolder = findPolicyHolder(this.memberDetails, false, true);
      return policyHolder?.insurableType === InsurableTypeEnum.RESPONSIBLE;
    },

    isPartnerAResponsibleAdult() {
      const partner = getPartnerDetailsMP(this.memberDetails);
      return partner?.insurableType === InsurableTypeEnum.RESPONSIBLE;
    },
  },

  methods: {
    ...mapMutations({}),

    async getReferenceData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;

      try {
        const response = await Promise.all([Services.getChangeCoverLandingPageReferenceData(), ChangeProductServices.getMembershipDetails(this.memberNumber)]);

        this.membershipInfo = response[1];
        this.memberDetails = this.membershipInfo.members;

        const res = response[0];
        this.content = {
          topContent: res.changeCoverInformationText.informationText,
          subHeading: res.changeCoverSubheadingTitleText.informationText,
          subHeadingContent: res.changeCoverSubheadingText.informationText,
          changeProductContent: res.changeYourProductSubheadingText.informationText,
          changeProductButton: res.changeYourProductButtonText.informationText,
          addRemovePersonContent: res.addOrRemovePersonSubheadingText.informationText,
          addRemovePersonButton: res.addOrRemovePersonButtonText.informationText,
          viewMemberDetailsContent: res.viewMemberDetailsSubheadingText.informationText,
          accessDeniedErrorText: res.accessDeniedErrorText.informationText,
          startDateEqualToCurrentDateErrorText: res.startDateEqualToCurrentDateErrorText.informationText,
        };
      } catch (err) {
        this.apiStatus.refData.fail = true;
        logger.error(err);
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    getButtonLink(link) {
      return link.replace(/~/i, '').tokenSwap();
    },

    navigateToPage(to, param = {}) {
      setNavigationState(param);
      this.$router.push({
        name: to,
      });
    },

    getImageUrl,
  },
};
</script>
<style lang="scss" scoped>
#page-content {
  @include flex-row;
  justify-content: space-between;
  column-gap: 10%;
  padding: 56px 0;

  @include mobile {
    flex-direction: column;
  }
}

.section-description {
  max-width: rem(700);
  margin-bottom: rem(50);
}

:deep(h3) {
  font-size: $DESKTOP_H4_SIZE;
}
</style>
